import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  IconButton,
  Alert,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Check as CheckIcon,
  Dataset as DatasetIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  InsertDriveFileOutlined as FileIcon,
} from '@mui/icons-material';
import { useLead } from '../../contexts/LeadContext';

// ─── Theme (aligned with CSVImport) ───────────────────────────────────────────

const T = {
  green: '#0a6b3e',
  greenLt: '#e5f5ec',
  greenMd: '#0d8a50',
  greenSf: 'rgba(10,107,62,0.06)',
  ink: '#0f1a13',
  ink2: '#253b2c',
  subtle: '#526b5c',
  muted: '#8eaa98',
  bg: '#f5f8f6',
  border: '#dce8e1',
  borderL: '#eaf1ec',
  white: '#ffffff',
  red: '#dc2626',
  redLt: '#fef2f2',
  redBorder: '#fecaca',
  amber: '#d97706',
  amberLt: '#fffbeb',
  blue: '#2563eb',
  blueLt: '#eff6ff',
  blueBorder: '#bfdbfe',
  font: "'Inter','DM Sans','Plus Jakarta Sans',sans-serif",
};

// ─── Constants ────────────────────────────────────────────────────────────────

const STEPS = ['Upload File', 'Validate & Preview', 'Done'];
const MAX_ROWS = 5000;

// ─── CSV Utilities ────────────────────────────────────────────────────────────

const sanitize = (value = '') =>
  String(value).replace(/^\ufeff/, '').replace(/^"(.*)"$/, '$1').trim();

const detectDelimiter = (text) => {
  const line = text.split(/\r?\n/)[0] || '';
  const candidates = [',', ';', '\t', '|'];
  let best = ',';
  let bestScore = -1;
  candidates.forEach((d) => {
    const score = line.split(d).length - 1;
    if (score > bestScore) { bestScore = score; best = d; }
  });
  return bestScore > 0 ? best : ',';
};

const parseCSV = (text) => {
  const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
  if (!normalized) throw new Error('CSV file is empty.');

  const delimiter = detectDelimiter(normalized);
  const lines = normalized.split('\n');

  const rawHeaders = lines[0].split(delimiter).map((h, i) => {
    let clean = sanitize(h);
    if (i === 0 && clean.charCodeAt(0) === 0xfeff) clean = clean.slice(1);
    return clean.toLowerCase();
  });

  const dataLines = lines.slice(1).filter((l) => l.trim());
  if (dataLines.length > MAX_ROWS)
    throw new Error(`CSV has ${dataLines.length} rows. Max allowed: ${MAX_ROWS.toLocaleString()}.`);

  const rows = [];
  const fieldErrors = [];

  dataLines.forEach((line, lineIdx) => {
    const values = line.split(delimiter).map(sanitize);
    const rowNum = lineIdx + 2;
    const row = {};
    rawHeaders.forEach((h, i) => { row[h] = values[i] ?? ''; });

    const errors = [];

    if (!row.name?.trim()) errors.push('Name is required');
    if (!row.source?.trim()) errors.push('Source is required');
    if (!row.email?.trim() && !row.mobile?.trim())
      errors.push('At least one of email or mobile is required');

    if (errors.length) {
      fieldErrors.push({ row_number: rowNum, row, reason: errors.join('; ') });
    } else {
      rows.push({ _row_number: rowNum, ...row });
    }
  });

  return { headers: rawHeaders, rows, fieldErrors };
};

// ─── Download helpers ─────────────────────────────────────────────────────────

const escapeCSV = (value) => {
  if (value == null) return '""';
  const s = String(value).replace(/"/g, '""');
  return `"${s}"`;
};

const downloadCSV = (filename, rows, extraColumns = []) => {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]).filter((k) => !k.startsWith('_'));
  const allHeaders = [...headers, ...extraColumns.filter((c) => !headers.includes(c))];
  const lines = [
    allHeaders.map(escapeCSV).join(','),
    ...rows.map((r) => allHeaders.map((h) => escapeCSV(r[h] ?? '')).join(',')),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const getRowField = (row, key) => {
  if (!row || typeof row !== 'object') return '';
  const v = row[key];
  if (v != null && String(v).trim() !== '') return String(v);
  const cap = key.charAt(0).toUpperCase() + key.slice(1);
  const alt = row[cap];
  return alt != null ? String(alt) : '';
};

// ─── Component ────────────────────────────────────────────────────────────────

const CSVUpdateLeadSource = ({ open, onClose, onComplete }) => {
  const { validateLeadSourceCSV, commitLeadSourceCSV } = useLead();

  const fileInputRef = useRef(null);

  const [activeStep, setActiveStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [csvData, setCsvData] = useState({ headers: [], rows: [], fieldErrors: [] });
  const [isValidating, setIsValidating] = useState(false);
  const [isCommitting, setIsCommitting] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [commitResult, setCommitResult] = useState(null);
  const [actionError, setActionError] = useState(null);

  const InfoChip = ({ icon, label, value, color = T.green, bg = T.greenLt }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75, borderRadius: '8px', bgcolor: bg }}>
      {icon}
      <Typography sx={{ fontFamily: T.font, fontSize: 12, fontWeight: 600, color }}>{value}</Typography>
      <Typography sx={{ fontFamily: T.font, fontSize: 11, color: T.subtle }}>{label}</Typography>
    </Box>
  );

  const reset = () => {
    setActiveStep(0);
    setSelectedFile(null);
    setFileError(null);
    setCsvData({ headers: [], rows: [], fieldErrors: [] });
    setIsValidating(false);
    setIsCommitting(false);
    setValidationResult(null);
    setCommitResult(null);
    setActionError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => { reset(); onClose(); };

  const handleFileChange = (e) => {
    setFileError(null);
    setActionError(null);
    setValidationResult(null);

    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      setFileError('Only .csv files are supported.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setFileError('File size must be under 5 MB.');
      return;
    }

    setSelectedFile(file);

    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = parseCSV(ev.target.result);
        setCsvData(parsed);
        setActiveStep(1);
      } catch (err) {
        setFileError(err.message);
        setSelectedFile(null);
        setCsvData({ headers: [], rows: [], fieldErrors: [] });
      }
    };
    reader.onerror = () => setFileError('Could not read the file.');
    reader.readAsText(file);
  };

  const handleValidate = async () => {
    if (!csvData.rows.length) {
      setActionError('No valid rows to send for validation.');
      return;
    }
    try {
      setIsValidating(true);
      setActionError(null);
      setValidationResult(null);

      const payload = csvData.rows.map(({ _row_number, ...rest }) => rest);
      const result = await validateLeadSourceCSV(payload);

      setValidationResult(result);
    } catch (err) {
      setActionError(err?.response?.data?.error || err.message || 'Validation failed.');
    } finally {
      setIsValidating(false);
    }
  };

  const handleCommit = async () => {
    if (!validationResult?.valid_rows?.length) {
      setActionError('No valid rows to commit.');
      return;
    }
    try {
      setIsCommitting(true);
      setActionError(null);

      const result = await commitLeadSourceCSV(validationResult.valid_rows);
      setCommitResult(result);
      setActiveStep(2);
      if (onComplete) onComplete(result);
    } catch (err) {
      setActionError(err?.response?.data?.error || err.message || 'Commit failed.');
    } finally {
      setIsCommitting(false);
    }
  };

  const handleDownloadTemplate = () => {
    const template = [
      'name,email,mobile,country_code,source',
      'Ananya Iyer,ananya.iyer@example.com,9765123409,+91,Google Ads',
      'Tanya Malhotra,,9986034123,+91,Facebook',
      'Dev Sharma,dev.sharma@example.com,,+91,Referral',
    ].join('\n');
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'lead_source_update_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownloadFailed = () => {
    const failed = [
      ...(csvData.fieldErrors || []),
      ...(validationResult?.failed_rows || []),
    ];
    if (!failed.length) return;
    downloadCSV(
      `failed_source_update_${new Date().toISOString().slice(0, 10)}.csv`,
      failed.map(({ row_number, row, reason }) => ({ row_number, ...(row || {}), reason })),
    );
  };

  const renderStep0 = () => (
    <Box sx={{ py: 2 }}>
      <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileChange} style={{ display: 'none' }} />

      <Box
        onClick={() => fileInputRef.current?.click()}
        sx={{
          border: `2px dashed ${T.border}`,
          borderRadius: 2.5,
          px: 3,
          py: 5,
          minHeight: 148,
          mb: 2,
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s',
          bgcolor: T.bg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '&:hover': { borderColor: T.green, bgcolor: T.greenSf },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
          <Box sx={{ width: 48, height: 48, borderRadius: '12px', bgcolor: T.greenLt, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <UploadIcon sx={{ fontSize: 24, color: T.green }} />
          </Box>
          <Box sx={{ textAlign: 'left' }}>
            <Typography sx={{ fontFamily: T.font, fontSize: 13.5, fontWeight: 600, color: T.ink }}>
              Click to select a CSV file
            </Typography>
            <Typography sx={{ fontFamily: T.font, fontSize: 11.5, color: T.muted }}>
              Max {MAX_ROWS.toLocaleString()} rows &middot; 5 MB &middot; .csv
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
        <Box sx={{ flex: 1, px: 1.5, py: 1, borderRadius: 1.5, border: `1px solid ${T.redBorder}`, bgcolor: T.redLt }}>
          <Typography sx={{ fontFamily: T.font, fontSize: 10.5, fontWeight: 700, color: T.red, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.25 }}>Required</Typography>
          <Typography sx={{ fontFamily: T.font, fontSize: 12, color: T.ink2, lineHeight: 1.5 }}>
            <strong>name</strong>, <strong>source</strong>, and <strong>email</strong> or <strong>mobile</strong>
          </Typography>
        </Box>
        <Box sx={{ flex: 1, px: 1.5, py: 1, borderRadius: 1.5, border: `1px solid ${T.border}`, bgcolor: T.greenLt }}>
          <Typography sx={{ fontFamily: T.font, fontSize: 10.5, fontWeight: 700, color: T.green, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.25 }}>Optional</Typography>
          <Typography sx={{ fontFamily: T.font, fontSize: 12, color: T.ink2, lineHeight: 1.5 }}>
            <strong>country_code</strong> (defaults to +91)
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          mb: 1.5,
          borderRadius: 2,
          border: `1px solid ${T.borderL}`,
          bgcolor: T.white,
          overflow: 'hidden',
          boxShadow: '0 1px 2px rgba(15, 26, 19, 0.04)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.75, bgcolor: T.bg, borderBottom: `1px solid ${T.borderL}` }}>
          <InfoIcon sx={{ fontSize: 15, color: T.green, flexShrink: 0 }} />
          <Typography sx={{ fontFamily: T.font, fontSize: 9.5, fontWeight: 700, color: T.subtle, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            How this update works
          </Typography>
        </Box>
        <Box component="ul" sx={{ m: 0, py: 1, px: 1.5, pl: 2.75, fontFamily: T.font, color: T.ink2, fontSize: 11, lineHeight: 1.55, listStyleType: 'disc' }}>
          <Typography component="li" sx={{ mb: 0.5, pl: 0.5, display: 'list-item', fontSize: 11 }}>
            Rows are <strong>matched</strong> to existing contacts by email or mobile (plus country code for mobile), then to an active lead for that contact.
          </Typography>
          <Typography component="li" sx={{ mb: 0.5, pl: 0.5, display: 'list-item', fontSize: 11 }}>
            The <strong>source</strong> value in the CSV replaces the stored source on both the <strong>lead</strong> and the <strong>contact</strong>.
          </Typography>
          <Typography component="li" sx={{ pl: 0.5, display: 'list-item', mb: 0, fontSize: 11 }}>
            If the lead already has that same source (after normalization), the row is <strong>skipped</strong> — not counted as an error.
          </Typography>
        </Box>
      </Box>

      {fileError && (
        <Alert severity="error" sx={{ mb: 0, fontFamily: T.font, fontSize: 12.5, borderRadius: 2, '& .MuiAlert-message': { whiteSpace: 'pre-wrap' } }}>
          {fileError}
        </Alert>
      )}
    </Box>
  );

  const renderStep1 = () => {
    const totalRows = csvData.rows.length + csvData.fieldErrors.length;
    const clientFailed = csvData.fieldErrors.length;
    const serverValid = validationResult?.total_valid ?? null;
    const serverFailed = validationResult?.total_failed ?? null;
    const allFailed = [...(csvData.fieldErrors || []), ...(validationResult?.failed_rows || [])];
    const canCommit = validationResult && serverValid > 0;

    return (
      <Box sx={{ py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, borderRadius: 2, bgcolor: T.bg, border: `1px solid ${T.borderL}`, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: T.greenLt, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FileIcon sx={{ fontSize: 18, color: T.green }} />
            </Box>
            <Box>
              <Typography sx={{ fontFamily: T.font, fontSize: 13, fontWeight: 600, color: T.ink }}>{selectedFile?.name}</Typography>
              <Typography sx={{ fontFamily: T.font, fontSize: 11, color: T.muted }}>
                {(selectedFile?.size / 1024).toFixed(1)} KB &middot; {totalRows} rows
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <InfoChip label="total" value={totalRows} />
            {clientFailed > 0 && (
              <InfoChip label="client errors" value={clientFailed} color={T.amber} bg={T.amberLt} icon={<ErrorIcon sx={{ fontSize: 14, color: T.amber }} />} />
            )}
            {serverValid !== null && <InfoChip label="ready" value={serverValid} />}
            {serverFailed !== null && serverFailed > 0 && (
              <InfoChip label="server errors" value={serverFailed} color={T.red} bg={T.redLt} icon={<ErrorIcon sx={{ fontSize: 14, color: T.red }} />} />
            )}
          </Box>
        </Box>

        {actionError && (
          <Alert severity="error" sx={{ mb: 2, fontFamily: T.font, fontSize: 12.5, borderRadius: 2 }}>
            {actionError}
          </Alert>
        )}

        {csvData.rows.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontFamily: T.font, fontSize: 12, fontWeight: 700, color: T.subtle, mb: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Preview (first 20 valid rows)
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 240, borderRadius: 2, border: `1px solid ${T.borderL}`, boxShadow: 'none' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontFamily: T.font, fontWeight: 700, fontSize: 11, color: T.subtle, bgcolor: T.bg, position: 'sticky', left: 0, zIndex: 3, minWidth: 44 }}>#</TableCell>
                    {csvData.headers.map((h) => (
                      <TableCell key={h} sx={{ fontFamily: T.font, fontWeight: 700, fontSize: 11, color: T.subtle, bgcolor: T.bg, whiteSpace: 'nowrap' }}>
                        {h}
                        {['name', 'source', 'email', 'mobile'].includes(h) && (
                          <Typography component="span" sx={{ color: T.red, fontSize: 9, ml: 0.3, verticalAlign: 'super' }}>*</Typography>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {csvData.rows.slice(0, 20).map((row) => (
                    <TableRow key={row._row_number} sx={{ '&:hover': { bgcolor: T.bg } }}>
                      <TableCell sx={{ fontFamily: T.font, fontSize: 11, color: T.muted, position: 'sticky', left: 0, bgcolor: T.white, zIndex: 2 }}>{row._row_number}</TableCell>
                      {csvData.headers.map((h) => (
                        <TableCell key={h} sx={{ fontFamily: T.font, fontSize: 12, color: T.ink2, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {row[h] ?? ''}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {csvData.rows.length > 20 && (
              <Typography sx={{ fontFamily: T.font, fontSize: 11, color: T.muted, mt: 0.5 }}>
                Showing 20 of {csvData.rows.length} valid rows.
              </Typography>
            )}
          </Box>
        )}

        {allFailed.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ fontFamily: T.font, fontSize: 12, fontWeight: 700, color: T.red, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Rows with errors ({allFailed.length})
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
                onClick={handleDownloadFailed}
                sx={{ fontFamily: T.font, fontSize: 11, textTransform: 'none', borderColor: T.border, color: T.subtle, borderRadius: 2, '&:hover': { borderColor: T.green, color: T.green } }}
              >
                Download errors CSV
              </Button>
            </Box>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 260, borderRadius: 2, border: `1px solid ${T.redBorder}`, boxShadow: 'none' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {['Row', 'Name', 'Email', 'Mobile', 'Source', 'Reason'].map((h) => (
                      <TableCell key={h} sx={{ fontFamily: T.font, fontWeight: 700, fontSize: 11, color: T.subtle, bgcolor: T.redLt, borderBottom: `1px solid ${T.redBorder}` }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allFailed.map((item, i) => {
                    const row = item.row || item;
                    return (
                      <TableRow key={i} sx={{ '&:hover': { bgcolor: T.redLt } }}>
                        <TableCell sx={{ fontFamily: T.font, fontSize: 12, color: T.muted }}>{item.row_number ?? '—'}</TableCell>
                        <TableCell sx={{ fontFamily: T.font, fontSize: 12, color: T.ink2 }}>{getRowField(row, 'name') || '—'}</TableCell>
                        <TableCell sx={{ fontFamily: T.font, fontSize: 12, color: T.ink2, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{getRowField(row, 'email') || '—'}</TableCell>
                        <TableCell sx={{ fontFamily: T.font, fontSize: 12, color: T.ink2, fontVariantNumeric: 'tabular-nums' }}>{getRowField(row, 'mobile') || '—'}</TableCell>
                        <TableCell sx={{ fontFamily: T.font, fontSize: 12, color: T.ink2 }}>{getRowField(row, 'source') || '—'}</TableCell>
                        <TableCell sx={{ fontFamily: T.font, fontSize: 12, color: T.red, fontWeight: 500, whiteSpace: 'normal', maxWidth: 280, wordBreak: 'break-word' }}>
                          {item.reason || item.error || '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {isCommitting && (
          <LinearProgress sx={{ mb: 2, height: 4, borderRadius: 2, bgcolor: T.borderL, '& .MuiLinearProgress-bar': { bgcolor: T.green, borderRadius: 2 } }} />
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap', mt: 1 }}>
          <Button
            onClick={() => { setActiveStep(0); setActionError(null); }}
            disabled={isValidating || isCommitting}
            sx={{ fontFamily: T.font, fontSize: 12.5, fontWeight: 500, color: T.subtle, textTransform: 'none', borderRadius: 2 }}
          >
            Back
          </Button>
          <Button
            variant="outlined"
            startIcon={<CheckIcon sx={{ fontSize: 16 }} />}
            onClick={handleValidate}
            disabled={!csvData.rows.length || isValidating || isCommitting || !!fileError || !!actionError}
            sx={{
              fontFamily: T.font,
              fontSize: 12.5,
              fontWeight: 600,
              textTransform: 'none',
              borderColor: T.green,
              color: T.green,
              borderRadius: 2,
              '&:hover': { bgcolor: T.greenSf, borderColor: T.greenMd },
              '&.Mui-disabled': { borderColor: T.borderL, color: T.muted },
            }}
          >
            {isValidating ? 'Validating…' : 'Validate'}
          </Button>
          <Button
            variant="contained"
            startIcon={<UploadIcon sx={{ fontSize: 16 }} />}
            onClick={handleCommit}
            disabled={!canCommit || isCommitting || isValidating}
            sx={{
              fontFamily: T.font,
              fontSize: 12.5,
              fontWeight: 600,
              textTransform: 'none',
              bgcolor: T.green,
              borderRadius: 2,
              boxShadow: 'none',
              '&:hover': { bgcolor: T.greenMd, boxShadow: 'none' },
              '&.Mui-disabled': { bgcolor: T.borderL, color: T.muted },
            }}
          >
            {isCommitting ? 'Updating…' : `Update source (${serverValid ?? 0} rows)`}
          </Button>
        </Box>
      </Box>
    );
  };

  const renderStep2 = () => (
    <Box sx={{ py: 4, textAlign: 'center' }}>
      <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: T.greenLt, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
        <CheckIcon sx={{ fontSize: 32, color: T.green }} />
      </Box>
      <Typography sx={{ fontFamily: T.font, fontSize: 16, fontWeight: 700, color: T.ink, mb: 0.5 }}>
        {commitResult?.message || 'Source update complete'}
      </Typography>
      <Typography sx={{ fontFamily: T.font, fontSize: 13, color: T.subtle, mb: 3, maxWidth: 400, mx: 'auto' }}>
        {commitResult?.total_updated ?? 0} lead(s) updated successfully.
      </Typography>
      <Button
        variant="contained"
        onClick={handleClose}
        sx={{
          fontFamily: T.font,
          fontSize: 13,
          fontWeight: 600,
          textTransform: 'none',
          bgcolor: T.green,
          borderRadius: 2,
          px: 4,
          boxShadow: 'none',
          '&:hover': { bgcolor: T.greenMd, boxShadow: 'none' },
        }}
      >
        Done
      </Button>
    </Box>
  );

  const stepContent = [renderStep0, renderStep1, renderStep2];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { maxWidth: 600, width: '100%', maxHeight: '85vh', borderRadius: 3, fontFamily: T.font } }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: T.greenLt, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <DatasetIcon sx={{ fontSize: 18, color: T.green }} />
            </Box>
            <Typography sx={{ fontFamily: T.font, fontSize: 16, fontWeight: 700, color: T.ink }}>
              Update lead source from CSV
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
              onClick={handleDownloadTemplate}
              sx={{
                fontFamily: T.font,
                fontSize: 11.5,
                fontWeight: 600,
                textTransform: 'none',
                borderColor: T.border,
                color: T.subtle,
                borderRadius: 2,
                py: 0.5,
                px: 1.5,
                '&:hover': { borderColor: T.green, color: T.green, bgcolor: T.greenSf },
              }}
            >
              Sample CSV
            </Button>
            <IconButton size="small" onClick={handleClose} sx={{ color: T.muted, '&:hover': { color: T.ink } }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <Divider sx={{ borderColor: T.borderL }} />

      <DialogContent sx={{ px: 3, py: 0 }}>
        <Stepper
          activeStep={activeStep}
          sx={{
            pt: 2,
            pb: 1,
            '& .MuiStepIcon-root': { color: T.borderL },
            '& .MuiStepIcon-root.Mui-active': { color: T.green },
            '& .MuiStepIcon-root.Mui-completed': { color: T.green },
            '& .MuiStepLabel-label': { fontFamily: T.font, fontSize: 12.5, fontWeight: 500, color: T.muted },
            '& .MuiStepLabel-label.Mui-active': { color: T.ink, fontWeight: 600 },
            '& .MuiStepLabel-label.Mui-completed': { color: T.green, fontWeight: 600 },
            '& .MuiStepConnector-line': { borderColor: T.borderL },
          }}
        >
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {stepContent[activeStep]?.()}
      </DialogContent>
    </Dialog>
  );
};

export default CSVUpdateLeadSource;
